import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DeleteClusterDangerZone } from '@/components/DeleteClusterDangerZone/DeleteClusterDangerZone'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useCluster } from '@/contexts/useCluster'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { HivesOverviewClusterCard } from '@/features/HivesOverview/components/HivesOverviewClusterCard'
import { flattenOrganizationHivesResponse } from '@/features/HivesOverview/util'
import { useFindHivesByOrgId } from '@/generated/http-clients/v2ApiComponents'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import { Button, Icon, Skeleton } from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute } from '@tanstack/react-router'
import { FileText } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { hivesOverviewOrganizationDetailsRoute } from './HivesOverviewOrganizationDetails'

export const hivesOverviewClusterDetailsRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/orgs/$orgId/$clusterId',
  component: HivesOverviewsClusterDetails,
})

export function HivesOverviewsClusterDetails() {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { t: tFeatures } = useTranslation('features', { useSuspense: false })
  const pathParams = hivesOverviewClusterDetailsRoute.useParams()
  const clusterContext = useCluster()

  const hivesRequest = useFindHivesByOrgId({
    pathParams: {
      orgId: pathParams.orgId,
    },
  })

  const flattenedData = useMemo(() => {
    if (hivesRequest.data) {
      return flattenOrganizationHivesResponse(hivesRequest.data)
    }
    return []
  }, [hivesRequest.data])

  const currentCluster = useMemo(() => {
    if (flattenedData) {
      return flattenedData.find(
        (cluster) => cluster.hive_info?.cluster_id === pathParams.clusterId,
      )
    }
  }, [flattenedData, pathParams.clusterId])

  const breadCrumbs = [
    { name: t('sidebar.runningHives'), path: hivesOverviewRootRoute.to },
    {
      name: pathParams.orgId,
      path: hivesOverviewOrganizationDetailsRoute.to,
      pathParams: {
        orgId: pathParams.orgId,
      },
    },
    {
      name: pathParams.clusterId,
      path: hivesOverviewClusterDetailsRoute.to,
    },
  ]

  // Render the cluster card
  return (
    <PageLayout
      title="Cluster Overview"
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <Link
          to={auditLogsRoute.to}
          search={{ hiveId: pathParams.clusterId, dateRange: 'last24Hours' }}
        >
          <Button variant="outline" colorPalette="gray" size="sm">
            <Icon as={FileText} />
            {String(tFeatures('auditLogs.contextualAccess.viewAuditLogs'))}
          </Button>
        </Link>
      }
    >
      {hivesRequest.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {hivesRequest.isError || !hivesRequest.data ? (
        <ApiErrorState
          apiResource={hivesRequest as unknown as UseQueryResult}
          errorTitle="An error happened"
          errorDescription="We failed to load the data from the server for the Hive Details."
        />
      ) : null}
      {currentCluster ? (
        <HivesOverviewClusterCard cluster={currentCluster} />
      ) : null}

      <DeleteClusterDangerZone
        orgId={pathParams.orgId}
        clusterId={pathParams.clusterId}
        clusterName={currentCluster?.hive_info?.cluster_id}
        onDelete={clusterContext.deleteOrgCluster}
      />
    </PageLayout>
  )
}
